import {
  fuzzyMatchRegex,
  fuzzyMatchScore,
  fuzzyMatchWithMapping,
} from '@wandb/weave/common/util/fuzzyMatch';
import {type SetState} from '@wandb/weave/common/util/types';
import {clsx} from 'clsx';
import React, {type JSX, useMemo, useState} from 'react';
import ReactTable, {type Column, type TableProps} from 'react-table';

import {DEFAULT_PAGE_SIZE_OPTIONS, useWBReactTablePageSize} from './util';
import {WBReactTableFooter} from './WBReactTableFooter';
import {WBReactTableHeader} from './WBReactTableHeader';

type PassProps = Partial<
  Pick<
    TableProps,
    | `loading`
    | `getTrProps`
    | `sortable`
    | `defaultSorted`
    | `onSortedChange`
    | `defaultSortMethod`
  >
>;

export type DataRow = {
  searchString?: string;
  row: any;
};

export type WBReactTablePaginationMode = 'client' | 'server';

type WBReactTableCommonProps = {
  /**
   * `data` is a dictionary that has string values of each key for each row.
   *
   * `getSearchResultColumns` is a function that returns a different set of
   * column renderers to use when there is an active search query.
   *
   * `onSearch`, if supplied, fires when the user enters input into the search bar.
   * No local search will be performed if specified.
   *
   * `paginationMode="server"` switches pagination to caller-managed data:
   * local slicing is bypassed, `data` is treated as an already fetched page,
   * and page/page size/total count must all come from the caller.
   */
  className?: string;
  data: DataRow[];
  columns: Column[];
  getSearchResultColumns?: (searchQuery: string) => Column[];
  noPaging?: boolean;
  noSearch?: boolean;
  noHeader?: boolean;
  hasNextPage?: boolean;
  onFetchNextPage?: () => void;
  onSearch?: (query: string) => void;
  fetchingNextPage?: boolean;
  pageSizeOptions?: number[];
  extraActions?: React.ReactNode;
  headerInfo?: JSX.Element;
  style?: React.CSSProperties;
  resizable?: boolean;
  dataTest?: string;
  searchPlaceholder?: string;
  renderHiddenRow?: (rowData: DataRow['row'], i: number) => React.ReactNode;
  noDataComponent?: () => React.JSX.Element;
  getSearchOptions?: (row: DataRow['row']) => string[]; // Use when wanting to search multiple columns of the row at once
  showPaginationBottom?: boolean;
  renderShimmerRow?: (rowIndex: number) => DataRow['row'];
} & PassProps;

type WBReactTableClientPaginationProps = {
  paginationMode?: 'client';
  pageSize?: number;
  onChangePageSize?: (pageSize: number) => void;
  page?: never;
  onPageChange?: never;
  totalCount?: never;
};

type WBReactTableServerPaginationProps = {
  paginationMode: 'server';
  pageSize: number;
  onChangePageSize: (pageSize: number) => void;
  page: number;
  onPageChange: (page: number) => void;
  totalCount: number;
};

export type WBReactTableProps = WBReactTableCommonProps &
  (WBReactTableClientPaginationProps | WBReactTableServerPaginationProps);

// A wrapper around react-table that adds our paging and search.
const WBReactTable: React.FC<WBReactTableProps> = props => {
  const {
    className,
    data,
    columns,
    getSearchResultColumns,
    noPaging = false,
    noSearch = false,
    noHeader = false,
    pageSize: defaultPageSize,
    onChangePageSize,
    hasNextPage = false,
    onFetchNextPage,
    onSearch,
    fetchingNextPage = false,
    pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
    extraActions,
    headerInfo,
    style,
    resizable = false,
    dataTest,
    searchPlaceholder,
    renderHiddenRow,
    noDataComponent,
    getSearchOptions,
    showPaginationBottom = false,
    renderShimmerRow,
    ...passProps
  } = props;
  const isServerPaginated = props.paginationMode === 'server';
  const showShimmerRows =
    renderShimmerRow != null && passProps.loading === true;
  const shimmerRowCount = isServerPaginated
    ? props.pageSize
    : (defaultPageSize ?? DEFAULT_PAGE_SIZE_OPTIONS[0]);
  const tableData = useMemo<DataRow[]>(() => {
    if (!showShimmerRows || renderShimmerRow == null) {
      return data;
    }

    return Array.from({length: shimmerRowCount}, (_, rowIndex) => ({
      row: renderShimmerRow(rowIndex),
    }));
  }, [data, renderShimmerRow, shimmerRowCount, showShimmerRows]);
  // When we render custom loading rows, hide react-table's built-in
  // "Loading..." overlay by not passing the flag through.
  const reactTablePassProps = showShimmerRows
    ? {...passProps, loading: false}
    : passProps;
  const [searchQuery, setSearchQuery] = useState('');
  const [internalPage, setInternalPage] = useState(0);

  const effectivePage = isServerPaginated ? props.page : internalPage;
  const setPage = useMemo<SetState<number>>(() => {
    if (!isServerPaginated) {
      return setInternalPage;
    }
    const {onPageChange, page} = props;
    return updater => {
      const next =
        typeof updater === 'function'
          ? (updater as (prev: number) => number)(page)
          : updater;
      onPageChange(next);
    };
  }, [isServerPaginated, props]);

  // Rows

  const rows = useMemo(() => {
    if (showShimmerRows) {
      return tableData.map(x => x.row);
    }

    if (getSearchOptions && searchQuery) {
      const filteredData = tableData
        .map(({row}) => ({row, searchOptions: getSearchOptions(row)}))
        .filter(
          ({searchOptions}) =>
            searchOptions.find(
              str => str != null && str.match(fuzzyMatchRegex(searchQuery))
            ) != null
        );

      return filteredData
        .sort(
          (
            {searchOptions: searchOptions1},
            {searchOptions: searchOptions2}
          ) => {
            // Prefer exact match
            if (searchOptions1.includes(searchQuery)) {
              return 1;
            }
            if (searchOptions2.includes(searchQuery)) {
              return -1;
            }

            // Else sort by fuzzy match score
            const fuzzyMatchScore1 = Math.max(
              ...searchOptions1.map(str => fuzzyMatchScore(str, searchQuery))
            );
            const fuzzyMatchScore2 = Math.max(
              ...searchOptions2.map(str => fuzzyMatchScore(str, searchQuery))
            );
            return fuzzyMatchScore1 - fuzzyMatchScore2;
          }
        )
        .map(({row}) => row);
    }
    const selectedData = searchQuery
      ? fuzzyMatchWithMapping(tableData, searchQuery, d => d.searchString ?? '')
      : tableData;
    return selectedData.map(x => x.row);
  }, [tableData, showShimmerRows, searchQuery, getSearchOptions]);

  const rowCount = rows.length;

  // Columns

  const modifiedColumns = useMemo(() => {
    if (!searchQuery || getSearchResultColumns == null) {
      return columns;
    }
    return getSearchResultColumns(searchQuery);
  }, [columns, searchQuery, getSearchResultColumns]);

  // Page size

  const {pageSize: internalPageSize, setPageSize} = useWBReactTablePageSize({
    rowCount,
    defaultPageSize,
    noPaging,
  });

  const pageSize = isServerPaginated ? props.pageSize : internalPageSize;
  const paginationRowCount = isServerPaginated ? props.totalCount : rowCount;
  // During the initial server-paginated load, totalCount is still unknown.
  // Hide the row summary so we don't briefly show "0-0 of 0".
  const hideRowSummary =
    isServerPaginated && passProps.loading === true && paginationRowCount === 0;

  const changePageSize = (newPageSize: number) => {
    if (!isServerPaginated) {
      setPageSize(newPageSize);
    }
    onChangePageSize?.(newPageSize);
  };

  // Hidden rows (we may want to render them for SEO purposes)

  const renderHiddenRows = (shownRowsStart: number, shownRowsEnd: number) => {
    if (renderHiddenRow == null) {
      return null;
    }
    const hiddenRows = [
      ...rows.slice(0, shownRowsStart),
      ...rows.slice(shownRowsEnd),
    ];
    return hiddenRows.map(renderHiddenRow);
  };

  // In server-paginated mode, react-table sees the data as a single page
  // (the caller has already done the slicing). We use a separate
  // pageSize/page for react-table so its slicing is a no-op, while the
  // header pagination control still drives the caller via setPage.
  const reactTablePage = isServerPaginated ? 0 : effectivePage;
  const reactTablePageSize = isServerPaginated
    ? Math.max(rowCount, 1)
    : pageSize;

  return (
    <div
      className={clsx('wb-react-table', className)}
      data-test={dataTest}
      style={style ?? {}}>
      <ReactTable
        {...reactTablePassProps}
        data={rows}
        columns={modifiedColumns}
        page={reactTablePage}
        pageSize={reactTablePageSize}
        minRows={0}
        resizable={resizable}
        pageSizeOptions={pageSizeOptions}
        showPaginationTop={false}
        showPaginationBottom={false}
        NoDataComponent={noDataComponent}
        showPageJump={false}>
        {/* ReactTable takes a render function as `children` so that
          it can make `reactTableState` and `makeTable` available
          when constructing the contents. */}
        {(reactTableState, makeTable) => {
          return (
            <>
              {!noHeader && (
                <WBReactTableHeader
                  {...{
                    page: effectivePage,
                    pageSize,
                    pageSizeOptions,
                    rowCount: paginationRowCount,
                    noSearch,
                    noPaging,
                    extraActions,
                    headerInfo,
                    searchPlaceholder,
                    changePageSize,
                    hasNextPage,
                    fetchingNextPage,
                    onFetchNextPage,
                    setPage,
                    onSearch: onSearch ?? setSearchQuery,
                    setSearchQuery,
                    hideRowSummary,
                  }}
                />
              )}
              {makeTable()}
              {showPaginationBottom && (
                <WBReactTableFooter
                  {...{
                    page: effectivePage,
                    pageSize,
                    pageSizeOptions,
                    rowCount: paginationRowCount,
                    changePageSize,
                    hasNextPage,
                    fetchingNextPage,
                    onFetchNextPage,
                    setPage,
                    hideRowSummary,
                  }}
                />
              )}
              {!showShimmerRows &&
                renderHiddenRows(
                  reactTableState.startRow,
                  reactTableState.endRow
                )}
            </>
          );
        }}
      </ReactTable>
    </div>
  );
};

export default WBReactTable;
